import React, { useEffect, useState, useMemo } from 'react';
import { UserPlus, Trash2, X, AlertCircle, CheckCircle } from 'lucide-react';
import api from '../services/api';

// Hoisted modal — keeps focus when typing into inputs (see ClientList lesson).
const Modal = ({ show, onClose, title, children }) => {
  if (!show) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="card w-full max-w-md">
        <div className="flex items-center justify-between border-b border-black/10 p-4">
          <h3 className="text-lg font-semibold text-ink-100">{title}</h3>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
};

// Roles a client_admin is allowed to provision inside their own org.
// super_admin / client_admin are deliberately excluded — those are
// org-owner / platform roles, not workforce roles.
const ASSIGNABLE_ROLES = [
  { value: 'recruiter', label: 'Recruiter', desc: 'Manages requirements, candidates, and campaigns.' },
];

const ROLE_BADGE = {
  recruiter:    'bg-blue-500/15 text-blue-700 border-blue-500/30',
  client_admin: 'bg-brand-500/15 text-brand-700 border-brand-500/30',
};

export default function Team() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ email: '', full_name: '', role: 'recruiter', password: '' });
  const [submitting, setSubmitting] = useState(false);

  const me = useMemo(() => {
    try { return JSON.parse(atob(localStorage.getItem('admin_token').split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))); }
    catch { return {}; }
  }, []);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.get('/users/list-org');
      setUsers(r.data?.users || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load team members.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setSubmitting(true);
    try {
      await api.post('/users/register', { ...form, organization_id: me.organization_id });
      setSuccess(`Invited ${form.full_name} as ${form.role}.`);
      setForm({ email: '', full_name: '', role: 'recruiter', password: '' });
      setTimeout(() => { setShowAdd(false); setSuccess(''); }, 1500);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not create user.');
    } finally { setSubmitting(false); }
  };

  const handleDeactivate = async (u) => {
    if (!window.confirm(`Deactivate ${u.full_name} (${u.email})? They will no longer be able to sign in.`)) return;
    try {
      await api.patch(`/users/${u.user_id}/deactivate`);
      setSuccess(`${u.full_name} deactivated.`);
      load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not deactivate.');
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-extrabold text-ink-100">Team</h2>
          <p className="mt-1 text-sm text-ink-400">
            People in your organization who can sign in. You can add recruiters to help manage
            requirements, candidates, and campaigns.
          </p>
        </div>
        <button onClick={() => setShowAdd(true)} className="btn-primary inline-flex items-center gap-2">
          <UserPlus className="h-4 w-4" /> Add user
        </button>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-red-600">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">{error}</span>
        </div>
      )}
      {success && (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-emerald-600">
          <CheckCircle className="h-4 w-4" />
          <span className="text-sm">{success}</span>
        </div>
      )}

      {loading ? (
        <div className="rounded-xl border border-black/10 bg-ink-900 p-8 text-center text-ink-400">Loading team…</div>
      ) : users.length === 0 ? (
        <div className="rounded-xl border border-black/10 bg-ink-900 p-8 text-center text-ink-400">
          No team members yet. Add your first one with the button above.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-black/10">
          <table className="w-full">
            <thead className="bg-ink-900">
              <tr className="text-left text-xs uppercase tracking-wide text-ink-400">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/10">
              {users.map((u) => (
                <tr key={u.user_id} className="text-sm">
                  <td className="px-4 py-3 font-medium text-ink-100">{u.full_name || '—'}</td>
                  <td className="px-4 py-3 text-ink-300">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${ROLE_BADGE[u.role] || 'bg-black/5 text-ink-400 border-black/10'}`}>
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs ${u.is_active ? 'text-emerald-400' : 'text-ink-500'}`}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {u.role !== 'client_admin' && u.is_active && (
                      <button
                        onClick={() => handleDeactivate(u)}
                        className="text-ink-400 hover:text-red-400"
                        title="Deactivate"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal show={showAdd} onClose={() => setShowAdd(false)} title="Add team member">
        <form onSubmit={handleAdd} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-ink-300">Full name</label>
            <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="input" required />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-ink-300">Email</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" required />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-ink-300">Role</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="input">
              {ASSIGNABLE_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <p className="mt-1 text-xs text-ink-500">{ASSIGNABLE_ROLES.find((r) => r.value === form.role)?.desc}</p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-ink-300">Temporary password</label>
            <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input" minLength={8} required />
            <p className="mt-1 text-xs text-ink-500">Share this with them securely; they can change it after first sign-in.</p>
          </div>
          {error && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">{error}</div>
          )}
          {success && (
            <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-300">{success}</div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setShowAdd(false)} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={submitting} className="btn-primary">
              {submitting ? 'Creating…' : 'Create user'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
